import React, { useState, useEffect } from 'react';
import { driverAPI, rideAPI } from '../../lib/api';
import { formatCurrency, formatDateTime } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { MapPin, Navigation, Clock, DollarSign, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const AvailableRides = () => {
  const [loading, setLoading] = useState(true);
  const [rides, setRides] = useState([]);
  const [accepting, setAccepting] = useState(null);

  useEffect(() => {
    loadRides();
    const interval = setInterval(loadRides, 10000); // Refresh every 10s
    return () => clearInterval(interval);
  }, []);

  const loadRides = async () => {
    try {
      const response = await driverAPI.getAvailableRides();
      setRides(response.data);
    } catch (error) {
      console.error('Error loading rides:', error);
    } finally {
      setLoading(false);
    }
  };

  const acceptRide = async (rideId) => {
    setAccepting(rideId);
    try {
      await rideAPI.acceptRide(rideId);
      toast.success('Ride accepted!');
      loadRides();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept ride');
    } finally {
      setAccepting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="available-rides-page">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-display font-bold text-slate-900 dark:text-white uppercase tracking-tight mb-8">
          Available Rides
        </h1>

        {rides.length === 0 ? (
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardContent className="py-16 text-center">
              <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <Navigation className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                No rides available
              </h3>
              <p className="text-slate-500 dark:text-slate-400">
                New ride requests will appear here. Make sure you're online!
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {rides.map((ride) => (
              <Card 
                key={ride.id} 
                className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:shadow-md transition-shadow"
                data-testid={`ride-card-${ride.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex-1">
                      {/* Pickup */}
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                          <MapPin className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">Pickup</p>
                          <p className="font-medium text-slate-900 dark:text-white">
                            {ride.pickup_location?.address || `${ride.pickup_location?.lat?.toFixed(4)}, ${ride.pickup_location?.lng?.toFixed(4)}`}
                          </p>
                        </div>
                      </div>

                      {/* Dropoff */}
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                          <MapPin className="w-4 h-4 text-red-600" />
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">Dropoff</p>
                          <p className="font-medium text-slate-900 dark:text-white">
                            {ride.dropoff_location?.address || `${ride.dropoff_location?.lat?.toFixed(4)}, ${ride.dropoff_location?.lng?.toFixed(4)}`}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Ride Details */}
                    <div className="flex items-center gap-6 lg:gap-8">
                      <div className="text-center">
                        <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">Distance</p>
                        <p className="font-display font-bold text-lg text-slate-900 dark:text-white">
                          {ride.distance_miles?.toFixed(1)} mi
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">Time</p>
                        <p className="font-display font-bold text-lg text-slate-900 dark:text-white">
                          {ride.duration_minutes} min
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">Payout</p>
                        <p className="font-display font-bold text-lg text-green-600">
                          {formatCurrency(ride.driver_payout)}
                        </p>
                      </div>
                    </div>

                    {/* Accept Button */}
                    <Button
                      onClick={() => acceptRide(ride.id)}
                      disabled={accepting === ride.id}
                      className="rounded-full bg-red-600 hover:bg-red-700 font-bold uppercase tracking-wider px-8"
                      data-testid={`accept-ride-btn-${ride.id}`}
                    >
                      {accepting === ride.id ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        'Accept'
                      )}
                    </Button>
                  </div>

                  {/* Scheduled indicator */}
                  {ride.scheduled_time && (
                    <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-700">
                      <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
                        <Clock className="w-4 h-4" />
                        <span className="text-sm font-medium">
                          Scheduled for {formatDateTime(ride.scheduled_time)}
                        </span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AvailableRides;
